/**
 * Structural diff for plain JSON values that emits Immer-compatible patches.
 *
 * Immer's produceWithPatches(base, () => next) emits a single root "replace"
 * patch holding the entire next value, so it cannot be used to diff two
 * snapshots. This walks both trees and emits minimal add/remove/replace ops
 * that applyPatches() understands.
 */

/**
 * @typedef {{ op: 'add'|'remove'|'replace', path: (string|number)[], value?: unknown }} Patch
 * @typedef {(path: (string|number)[]) => boolean} IgnorePredicate
 */

function isPlainObject(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}

function clone(value) {
    return value === undefined ? undefined : JSON.parse(JSON.stringify(value));
}

export function deepEqual(a, b) {
    if (Object.is(a, b)) {
        return true;
    }
    if (Array.isArray(a)) {
        if (!Array.isArray(b) || a.length !== b.length) {
            return false;
        }
        for (let i = 0; i < a.length; i++) {
            if (!deepEqual(a[i], b[i])) {
                return false;
            }
        }
        return true;
    }
    if (isPlainObject(a) && isPlainObject(b)) {
        const aKeys = Object.keys(a);
        if (aKeys.length !== Object.keys(b).length) {
            return false;
        }
        for (const key of aKeys) {
            if (!Object.prototype.hasOwnProperty.call(b, key)) {
                return false;
            }
            if (!deepEqual(a[key], b[key])) {
                return false;
            }
        }
        return true;
    }
    return false;
}

function diffArrays(prev, next, path, out, ignore) {
    let start = 0;
    const maxPrefix = Math.min(prev.length, next.length);
    while (start < maxPrefix && deepEqual(prev[start], next[start])) {
        start++;
    }

    let prevEnd = prev.length;
    let nextEnd = next.length;
    while (
        prevEnd > start &&
        nextEnd > start &&
        deepEqual(prev[prevEnd - 1], next[nextEnd - 1])
    ) {
        prevEnd--;
        nextEnd--;
    }

    const prevCount = prevEnd - start;
    const nextCount = nextEnd - start;
    const shared = Math.min(prevCount, nextCount);

    for (let i = 0; i < shared; i++) {
        diffValues(prev[start + i], next[start + i], [...path, start + i], out, ignore);
    }
    // Inserts are emitted in ascending index order so each splice lands correctly.
    for (let i = shared; i < nextCount; i++) {
        out.push({ op: "add", path: [...path, start + i], value: clone(next[start + i]) });
    }
    // Removes all target the same index: each splice shifts the next one into place.
    for (let i = shared; i < prevCount; i++) {
        out.push({ op: "remove", path: [...path, start + shared] });
    }
}

function diffObjects(prev, next, path, out, ignore) {
    for (const key of Object.keys(prev)) {
        if (!Object.prototype.hasOwnProperty.call(next, key)) {
            const keyPath = [...path, key];
            if (!ignore?.(keyPath)) {
                out.push({ op: "remove", path: keyPath });
            }
        }
    }
    for (const key of Object.keys(next)) {
        const keyPath = [...path, key];
        if (ignore?.(keyPath)) {
            continue;
        }
        if (!Object.prototype.hasOwnProperty.call(prev, key)) {
            out.push({ op: "add", path: keyPath, value: clone(next[key]) });
        } else {
            diffValues(prev[key], next[key], keyPath, out, ignore);
        }
    }
}

function diffValues(prev, next, path, out, ignore) {
    if (Object.is(prev, next)) {
        return;
    }
    if (Array.isArray(prev) && Array.isArray(next)) {
        diffArrays(prev, next, path, out, ignore);
        return;
    }
    if (isPlainObject(prev) && isPlainObject(next)) {
        diffObjects(prev, next, path, out, ignore);
        return;
    }
    out.push({ op: "replace", path, value: clone(next) });
}

/**
 * @param {unknown} prev
 * @param {unknown} next
 * @param {{ ignore?: IgnorePredicate }} [options]
 * @returns {Patch[]}
 */
export function diffPatches(prev, next, { ignore } = {}) {
    /** @type {Patch[]} */
    const out = [];
    diffValues(prev, next, [], out, ignore);
    return out;
}
